export const MIN_EDITORIAL_COLLECTION_ITEMS_COUNT = 4;
export const MOBILE_ITEMS_PER_ROW = 2;
export const DESKTOP_ITEMS_PER_ROW = 4;

export const PERSONALIZED_COLLECTION_POSITIONS = [4, 11, 12, 26] as const;
export const INITIAL_EDITORIAL_COLLECTION_POSITIONS = [19] as const;
export const LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS = [2, 8, 14] as const;

export const MOBILE_PERSONALIZED_COLLECTION_POSITIONS = [4, 11, 14] as const;
export const MOBILE_INITIAL_EDITORIAL_COLLECTION_POSITIONS = [19] as const;
export const MOBILE_LOAD_MORE_PERSONALIZED_COLLECTION_POSITIONS = [0] as const;
export const MOBILE_LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS = [7, 14, 21] as const;

export const MASONRY_PROPS = {
    breakpointToNumColsMap: {
        desktop: 4,
        tabletLandscape: 4,
        tabletPortrait: 4,
        mobile: 2,
    },
    numCols: 4,
    elementGap: 18,
} as const;

export type HpSharedItemsGridCollectionSlotPositions = {
    personalized: readonly number[];
    editorial: readonly number[];
};

export function getHpSharedItemsGridCollectionSlotPositions({
    isMobile,
    isLoadMore,
}: {
    isMobile: boolean;
    isLoadMore: boolean;
}): HpSharedItemsGridCollectionSlotPositions {
    if (isMobile) {
        if (isLoadMore) {
            return {
                personalized: MOBILE_LOAD_MORE_PERSONALIZED_COLLECTION_POSITIONS,
                editorial: MOBILE_LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS,
            };
        }

        return {
            personalized: MOBILE_PERSONALIZED_COLLECTION_POSITIONS,
            editorial: MOBILE_INITIAL_EDITORIAL_COLLECTION_POSITIONS,
        };
    }

    if (isLoadMore) {
        return {
            personalized: [],
            editorial: LOAD_MORE_EDITORIAL_COLLECTION_POSITIONS,
        };
    }

    return {
        personalized: PERSONALIZED_COLLECTION_POSITIONS,
        editorial: INITIAL_EDITORIAL_COLLECTION_POSITIONS,
    };
}

const DESKTOP_FALLBACK_EDITOR_PICKS_COUNT = 4;
export const DESKTOP_INITIAL_EDITORS_PICKS_COUNT = 10;
const DESKTOP_INITIAL_FEATURED_ITEMS_COUNT = 10;
const DESKTOP_INITIAL_MOST_SAVED_COUNT = 7;

const DESKTOP_LOAD_MORE_MOST_SAVED_COUNT = 3;
const DESKTOP_LOAD_MORE_EXCELLENT_VALUES_COUNT = 10;
const DESKTOP_LOAD_MORE_BESTSELLING_COUNT = 8;

const MOBILE_INITIAL_EDITORS_PICKS_COUNT = 8;
const MOBILE_INITIAL_FEATURED_ITEMS_COUNT = 8;
const MOBILE_INITIAL_MOST_SAVED_COUNT = 6;

const MOBILE_LOAD_MORE_MOST_SAVED_COUNT = 2;
const MOBILE_LOAD_MORE_EXCELLENT_VALUES_COUNT = 9;
const MOBILE_LOAD_MORE_BESTSELLING_COUNT = 9;

export function getHpSharedItemsGridInitialFetchCounts(isMobile: boolean): {
    editorsPicksCount: number;
    featuredItemsCount: number;
    mostSavedCount: number;
} {
    if (isMobile) {
        return {
            editorsPicksCount: MOBILE_INITIAL_EDITORS_PICKS_COUNT,
            featuredItemsCount: MOBILE_INITIAL_FEATURED_ITEMS_COUNT,
            mostSavedCount: MOBILE_INITIAL_MOST_SAVED_COUNT,
        };
    }

    return {
        editorsPicksCount:
            DESKTOP_INITIAL_EDITORS_PICKS_COUNT + DESKTOP_FALLBACK_EDITOR_PICKS_COUNT,
        featuredItemsCount: DESKTOP_INITIAL_FEATURED_ITEMS_COUNT,
        mostSavedCount: DESKTOP_INITIAL_MOST_SAVED_COUNT,
    };
}

export function getHpSharedItemsGridLoadMoreFetchCounts(isMobile: boolean): {
    mostSavedCount: number;
    mostSavedPageStartOffset: number;
    excellentValuesCount: number;
    bestsellingDesignsCount: number;
} {
    if (isMobile) {
        return {
            mostSavedCount: MOBILE_LOAD_MORE_MOST_SAVED_COUNT,
            mostSavedPageStartOffset: MOBILE_INITIAL_MOST_SAVED_COUNT,
            excellentValuesCount: MOBILE_LOAD_MORE_EXCELLENT_VALUES_COUNT,
            bestsellingDesignsCount: MOBILE_LOAD_MORE_BESTSELLING_COUNT,
        };
    }

    return {
        mostSavedCount: DESKTOP_LOAD_MORE_MOST_SAVED_COUNT,
        mostSavedPageStartOffset: DESKTOP_INITIAL_MOST_SAVED_COUNT,
        excellentValuesCount: DESKTOP_LOAD_MORE_EXCELLENT_VALUES_COUNT,
        bestsellingDesignsCount: DESKTOP_LOAD_MORE_BESTSELLING_COUNT,
    };
}

// Grid recommendation slices skip the first 20 2-tower items already shown in You'll Want All of These section
const DESKTOP_FULL_2_TOWER_INITIAL_COUNT = 10;
const MOBILE_FULL_2_TOWER_INITIAL_COUNT = 8;

const DESKTOP_FULL_2_TOWER_LOAD_MORE_COUNT = 30;
const MOBILE_FULL_2_TOWER_LOAD_MORE_COUNT =
    MOBILE_LOAD_MORE_MOST_SAVED_COUNT +
    MOBILE_LOAD_MORE_EXCELLENT_VALUES_COUNT +
    MOBILE_LOAD_MORE_BESTSELLING_COUNT;

export function getFull2TowerPersonalizedRecommendationCounts(isMobile: boolean): {
    initialCount: number;
    loadMoreCount: number;
    fallbackCount: number;
} {
    if (isMobile) {
        return {
            initialCount: MOBILE_FULL_2_TOWER_INITIAL_COUNT,
            loadMoreCount: MOBILE_FULL_2_TOWER_LOAD_MORE_COUNT,
            fallbackCount: 0,
        };
    }

    return {
        initialCount: DESKTOP_FULL_2_TOWER_INITIAL_COUNT,
        loadMoreCount: DESKTOP_FULL_2_TOWER_LOAD_MORE_COUNT,
        fallbackCount: DESKTOP_FALLBACK_EDITOR_PICKS_COUNT,
    };
}

type Full2TowerPersonalizedRecommendationSlices = {
    initialItemIds: string[];
    fallbackItemIds: string[];
    loadMoreItemIds: string[];
};

export function getFull2TowerPersonalizedRecommendationSlices(
    recommendationIds: readonly string[],
    isMobile: boolean,
    youWantCount: number
): Full2TowerPersonalizedRecommendationSlices {
    const { initialCount, loadMoreCount, fallbackCount } =
        getFull2TowerPersonalizedRecommendationCounts(isMobile);
    const availableIds = recommendationIds.slice(youWantCount);

    const initialItemIds = availableIds.slice(0, initialCount);
    const loadMoreStart = initialCount;
    const loadMoreItemIds = availableIds.slice(loadMoreStart, loadMoreStart + loadMoreCount);
    const fallbackStart = loadMoreStart + loadMoreCount;
    const fallbackItemIds = availableIds.slice(fallbackStart, fallbackStart + fallbackCount);

    return { initialItemIds, fallbackItemIds, loadMoreItemIds };
}
